import React, { useState, useEffect } from 'react';
import {
  Box, Typography, Button, CircularProgress, Alert,
  Card, CardContent, Grid, Chip, List, ListItem, ListItemText, Divider
} from '@mui/material';
import {
  CreditCard as CardIcon,
  Launch as LaunchIcon,
  Warning as WarningIcon
} from '@mui/icons-material';
import AdminLayout from '../../components/admin/AdminLayout';
import { useAuth } from '@vortx/shared-ui';
import { API_BASE_URL } from '../../services/api';

const BillingPage = () => {
  const { currentUser, userProfile } = useAuth();
  const [billingStatus, setBillingStatus] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [portalLoading, setPortalLoading] = useState(false);

  const isSuperAdmin = userProfile?.is_super_admin;

  useEffect(() => {
    checkBillingStatus();
  }, []);

  const checkBillingStatus = async () => {
    try {
      setLoading(true);
      const response = await fetch(`${API_BASE_URL}/api/v1/billing/status`);
      const data = await response.json();
      setBillingStatus(data);
    } catch (err) {
      setError('Failed to check billing status');
    } finally {
      setLoading(false);
    }
  };

  const openBillingPortal = async () => {
    try {
      setPortalLoading(true);
      const token = await currentUser.getIdToken();
      const response = await fetch(`${API_BASE_URL}/api/v1/billing/portal`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          return_url: window.location.href
        })
      });

      if (!response.ok) {
        const data = await response.json();
        if (data.code === 'STRIPE_NOT_CONFIGURED') {
          setError('Billing is not configured yet. Contact support.');
          return;
        }
        throw new Error(data.error || 'Failed to open billing portal');
      }

      const data = await response.json();
      window.location.href = data.url;
    } catch (err) {
      setError(err.message);
    } finally {
      setPortalLoading(false);
    }
  };

  if (loading) {
    return (
      <AdminLayout title="Billing">
        <CircularProgress />
      </AdminLayout>
    );
  }

  return (
    <AdminLayout title="Billing" requireSuperAdmin={false}>
      {error && (
        <Alert severity="error" sx={{ mb: 3 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      <Grid container spacing={3} sx={{ alignItems: 'stretch' }}>
        {/* Billing Status Card */}
        <Grid item xs={12} md={6} sx={{ display: 'flex' }}>
          <Card sx={{ width: '100%', display: 'flex', flexDirection: 'column' }}>
            <CardContent sx={{ flex: 1 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
                <CardIcon color="primary" />
                <Typography variant="h6">Billing Status</Typography>
              </Box>

              {billingStatus?.configured ? (
                <Box>
                  <Chip
                    label="Active"
                    color="success"
                    size="small"
                    sx={{ mb: 2 }}
                  />
                  <Typography variant="body2" color="text.secondary">
                    Stripe billing is configured and ready.
                  </Typography>
                </Box>
              ) : (
                <Box>
                  <Chip
                    label="Not Configured"
                    color="warning"
                    size="small"
                    sx={{ mb: 2 }}
                    icon={<WarningIcon />}
                  />
                  <Typography variant="body2" color="text.secondary">
                    Billing is not yet configured. Contact your administrator to set up Stripe.
                  </Typography>
                </Box>
              )}
            </CardContent>
          </Card>
        </Grid>

        {/* Current Plan Card */}
        <Grid item xs={12} md={6} sx={{ display: 'flex' }}>
          <Card sx={{ width: '100%', display: 'flex', flexDirection: 'column' }}>
            <CardContent sx={{ flex: 1 }}>
              <Typography variant="h6" gutterBottom>Current Plan</Typography>
              <List dense>
                <ListItem>
                  <ListItemText
                    primary="Site DNA"
                    secondary={userProfile?.site_dna_plan || 'No subscription'}
                  />
                  <Chip
                    label={userProfile?.site_dna_plan ? 'Active' : 'None'}
                    color={userProfile?.site_dna_plan ? 'success' : 'default'}
                    size="small"
                  />
                </ListItem>
                <Divider />
                <ListItem>
                  <ListItemText
                    primary="Add-ons"
                    secondary={userProfile?.site_dna_add_ons?.join(', ') || 'None'}
                  />
                </ListItem>
              </List>
            </CardContent>
          </Card>
        </Grid>

        {/* Manage Billing Card */}
        <Grid item xs={12} md={isSuperAdmin ? 6 : 12} sx={{ display: 'flex' }}>
          <Card sx={{ width: '100%', display: 'flex', flexDirection: 'column' }}>
            <CardContent sx={{ flex: 1 }}>
              <Typography variant="h6" gutterBottom>Manage Billing</Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                Access the billing portal to update payment methods, view invoices, and manage your subscription.
              </Typography>

              <Button
                variant="contained"
                startIcon={portalLoading ? <CircularProgress size={20} /> : <LaunchIcon />}
                onClick={openBillingPortal}
                disabled={!billingStatus?.configured || portalLoading}
              >
                {portalLoading ? 'Opening...' : 'Open Billing Portal'}
              </Button>

              {!billingStatus?.configured && (
                <Typography variant="caption" display="block" sx={{ mt: 1, color: 'text.secondary' }}>
                  Billing portal is not available until Stripe is configured.
                </Typography>
              )}
            </CardContent>
          </Card>
        </Grid>

        {/* Super Admin: Subscription Management */}
        {isSuperAdmin && (
          <Grid item xs={12} md={6} sx={{ display: 'flex' }}>
            <Card sx={{ width: '100%', display: 'flex', flexDirection: 'column' }}>
              <CardContent sx={{ flex: 1 }}>
                <Typography variant="h6" gutterBottom>Subscription Management</Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                  Manage company subscriptions and seat allocations across all organizations.
                </Typography>
                <Button
                  variant="outlined"
                  href="/admin/subscriptions"
                >
                  Manage Subscriptions
                </Button>
              </CardContent>
            </Card>
          </Grid>
        )}
      </Grid>
    </AdminLayout>
  );
};

export default BillingPage;
